import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Unlink,
  Loader2,
  CheckCircle2,
  ArrowRight,
  AlertTriangle,
  Save,
  ChevronDown,
  ChevronUp,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { useXeroIntegration } from '../hooks/useXeroIntegration';
import { supabase, requireOrganizationId } from '../lib/supabase';
import { usePnlCategories } from '../hooks/usePnlCategories';
import { XeroLogo } from './IntegrationLogos';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ClaimsProject {
  id: string;
  job_location: string;
}

// These are known from the Xero Chart of Accounts CSV analysis
const XERO_ACCOUNT_SUGGESTIONS: { code: string; name: string; suggestedCategory: string }[] = [
  { code: '5-2100', name: 'Materials', suggestedCategory: 'Materials' },
  { code: '5-2150', name: 'Crane Hire', suggestedCategory: 'Misc items' },
  { code: '5-2200', name: 'Concrete Pumping Costs', suggestedCategory: 'Misc items' },
  { code: '5-2210', name: 'Scaffolding Costs', suggestedCategory: 'Scaffold contractors' },
  { code: '5-3100', name: 'SubContractors', suggestedCategory: 'Contractors' },
  { code: '5-4100', name: 'Engineering', suggestedCategory: 'Engineers' },
  { code: '5-4150', name: 'Handrail', suggestedCategory: 'Handrails & Stairs' },
  { code: '5-5100', name: 'Equipment Hire', suggestedCategory: 'Misc items' },
  { code: '5-6100', name: 'Labour Hire', suggestedCategory: 'Labour Hire' },
  { code: '5-7500', name: 'Stripping', suggestedCategory: 'Stripping of formwork' },
  { code: '6-5060', name: 'Wages & Salaries', suggestedCategory: 'Wages' },
  { code: '6-4040', name: 'Repairs & Maintenance - Yard', suggestedCategory: 'Yard' },
  { code: '6-4050', name: 'Repairs & Maintenance - Office', suggestedCategory: 'Office' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function XeroIntegrationCard() {
  const xero = useXeroIntegration();
  const { categories } = usePnlCategories();

  // Mapping state
  const [projects, setProjects] = useState<ClaimsProject[]>([]);
  const [projectMappings, setProjectMappings] = useState<Record<string, string>>({});
  const [accountMappings, setAccountMappings] = useState<Record<string, string>>({});
  const [showMappings, setShowMappings] = useState(false);
  const [syncMonths, setSyncMonths] = useState(6);

  // Known Xero tracking options from analysis
  // In production, these would be fetched from Xero API via the auth-xero endpoint
  const [xeroTrackingOptions] = useState<string[]>([
    'Aire', 'Akin', 'Arcare', 'Cascade', 'Cienna', 'Cleveland',
    'Cote', 'Esperance', 'Faro', 'Heran', 'Herston',
    'Income - Other', 'LaBelle', 'LaMer', 'MQStage2', 'Marquee',
    'Merivale', 'Office', 'OneBurleigh', 'Oria', 'Parkview',
    'Parkwood', 'Sanura', 'Springfield Lakes', 'Tapestry',
    'Windemere', 'Yard',
  ]);

  const loadProjects = useCallback(async () => {
    try {
      const orgId = requireOrganizationId();
      const { data } = await supabase
        .from('claims_projects')
        .select('id, job_location')
        .eq('organization_id', orgId)
        .order('job_location');
      setProjects(data || []);
    } catch { /* ignore */ }
  }, []);

  // Load status + projects on mount
  useEffect(() => {
    xero.checkStatus();
    loadProjects(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [loadProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  // When status loads, populate mappings from config
  useEffect(() => {
    if (xero.status.config) {
      setProjectMappings(xero.status.config.project_mappings || {}); // eslint-disable-line react-hooks/set-state-in-effect
      setAccountMappings(xero.status.config.account_mappings || {});
    }
  }, [xero.status.config]);

  const handleSaveMappings = useCallback(async () => {
    // Enrich account mappings with name-based keys for edge function matching.
    // Xero P&L reports only return account names (not codes), so the sync function
    // needs both "5-2100" → "Materials" AND "Materials" → "Materials" keys.
    const enrichedAccountMappings = { ...accountMappings };
    for (const suggestion of XERO_ACCOUNT_SUGGESTIONS) {
      const category = accountMappings[suggestion.code];
      if (category) {
        enrichedAccountMappings[suggestion.name] = category;
      }
    }
    await xero.saveMappings(projectMappings, enrichedAccountMappings);
  }, [xero, projectMappings, accountMappings]);

  const handleAutoMap = useCallback(() => {
    // Auto-populate account mappings from suggestions
    const newAccountMappings = { ...accountMappings };
    for (const suggestion of XERO_ACCOUNT_SUGGESTIONS) {
      if (!newAccountMappings[suggestion.code]) {
        newAccountMappings[suggestion.code] = suggestion.suggestedCategory;
      }
    }
    setAccountMappings(newAccountMappings);

    // Auto-populate project mappings using fuzzy match
    const newProjectMappings = { ...projectMappings };
    for (const project of projects) {
      if (newProjectMappings[project.id]) continue;
      const name = project.job_location.toLowerCase().split(' - ')[0].trim();
      const match = xeroTrackingOptions.find(
        opt => opt.toLowerCase() === name || opt.toLowerCase().includes(name)
      );
      if (match) {
        newProjectMappings[project.id] = match;
      }
    }
    setProjectMappings(newProjectMappings);
  }, [accountMappings, projectMappings, projects, xeroTrackingOptions]);

  // Loading
  if (xero.loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-3xl)' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
        </div>
      </div>
    );
  }

  const categoryNames = categories.map(c => typeof c === 'string' ? c : c.name);

  return (
    <div style={cardStyle}>
      {/* Brand accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: 'linear-gradient(90deg, #13B5EA 0%, #0078C1 60%, #005A9C 100%)',
        borderRadius: 'var(--border-radius-lg) var(--border-radius-lg) 0 0',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <div style={{
            filter: 'drop-shadow(0 2px 6px rgba(19, 181, 234, 0.25))',
          }}>
            <XeroLogo size={42} />
          </div>
          <div>
            <h2 style={{
              margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 700,
              color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              Xero
              <span style={{
                fontSize: 11, fontWeight: 500, padding: '2px 8px',
                borderRadius: 4, background: 'rgba(19, 181, 234, 0.08)',
                color: '#13B5EA', letterSpacing: '0.02em',
              }}>P&L Sync</span>
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Automatically sync Profit & Loss data into your projects.
            </p>
          </div>
        </div>
        {xero.status.connected && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 99,
            background: 'rgba(16,185,129,0.08)', color: '#10b981',
            border: '1px solid rgba(16,185,129,0.2)',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#10b981',
              boxShadow: '0 0 6px rgba(16,185,129,0.5)',
            }} />
            CONNECTED
          </span>
        )}
      </div>

      {/* Connection Status */}
      {xero.status.connected ? (
        <>
          {/* Connected state */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)',
            padding: 'var(--spacing-md)', background: 'rgba(16,185,129,0.06)',
            border: '1px solid rgba(16,185,129,0.15)', borderRadius: 'var(--border-radius-md)',
            marginBottom: 'var(--spacing-lg)',
          }}>
            <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>
                Connected to {xero.status.tenant_name || 'Xero'}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                P&L data will be synced to your projects based on mappings below.
              </div>
            </div>
            <button
              onClick={xero.disconnect}
              style={{
                ...buttonOutlineStyle,
                color: '#ef4444',
                borderColor: 'rgba(239,68,68,0.3)',
                fontSize: 12,
              }}
            >
              <Unlink size={14} />
              Disconnect
            </button>
          </div>

          {/* Sync Controls */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--border-radius-md)',
          }}>
            <button
              className="btn btn-primary"
              onClick={() => xero.syncPnl(syncMonths)}
              disabled={xero.syncing}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              {xero.syncing
                ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                : <RefreshCw size={16} />
              }
              {xero.syncing ? 'Syncing...' : 'Sync from Xero'}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Past</span>
              <select
                value={syncMonths}
                onChange={e => setSyncMonths(Number(e.target.value))}
                style={{
                  ...selectStyle,
                  width: 60,
                }}
              >
                {[3, 6, 12, 24].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>months</span>
            </div>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => {
                if (window.confirm('Remove ALL Xero-synced P&L data? Manual entries will be preserved.')) {
                  xero.clearXeroData();
                }
              }}
              disabled={xero.clearing}
              style={{
                ...buttonOutlineStyle,
                color: '#ef4444',
                borderColor: 'rgba(239,68,68,0.3)',
                fontSize: 12,
              }}
              title="Remove all Xero-synced data (beta)"
            >
              {xero.clearing
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Trash2 size={14} />
              }
              Clear Xero Data
            </button>
          </div>

          {/* Mappings Toggle */}
          <button
            onClick={() => setShowMappings(!showMappings)}
            style={{
              ...buttonOutlineStyle,
              width: '100%', justifyContent: 'space-between',
              marginBottom: showMappings ? 'var(--spacing-lg)' : 0,
            }}
          >
            <span style={{ fontWeight: 500 }}>Configure Mappings</span>
            {showMappings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showMappings && (
            <div style={{ display: 'grid', gap: 'var(--spacing-xl)' }}>
              {/* Auto-map button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)' }}>
                <button onClick={handleAutoMap} style={{ ...buttonOutlineStyle, fontSize: 12 }}>
                  ✨ Auto-Map Suggestions
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveMappings}
                  disabled={xero.savingMappings}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  {xero.savingMappings
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Save size={14} />
                  }
                  Save Mappings
                </button>
              </div>

              {/* Project Mapping */}
              <div>
                <h3 style={sectionHeaderStyle}>
                  Project Mapping
                  <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginLeft: 8 }}>
                    Claims App project → Xero tracking option
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {projects.map(project => (
                    <div key={project.id} style={mappingRowStyle}>
                      <div style={{ flex: 1, fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.job_location}
                        </div>
                      </div>
                      <ArrowRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <select
                        value={projectMappings[project.id] || ''}
                        onChange={e => {
                          setProjectMappings(prev => ({
                            ...prev,
                            [project.id]: e.target.value,
                          }));
                        }}
                        style={{ ...selectStyle, flex: 1, maxWidth: 220 }}
                      >
                        <option value="">(Not mapped)</option>
                        {xeroTrackingOptions
                          .filter(opt => !['Income - Other', 'Office', 'Yard'].includes(opt))
                          .map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))
                        }
                      </select>
                      {projectMappings[project.id] && (
                        <CheckCircle2 size={14} style={{ color: '#10b981', flexShrink: 0 }} />
                      )}
                      {!projectMappings[project.id] && (
                        <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  {Object.keys(projectMappings).filter(k => projectMappings[k]).length} of {projects.length} projects mapped
                </div>
              </div>

              {/* Account Mapping */}
              <div>
                <h3 style={sectionHeaderStyle}>
                  Account Mapping
                  <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginLeft: 8 }}>
                    Xero account → P&L category
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {XERO_ACCOUNT_SUGGESTIONS.map(account => (
                    <div key={account.code} style={mappingRowStyle}>
                      <div style={{ flex: 1, fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', minWidth: 0 }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginRight: 6 }}>{account.code}</span>
                        {account.name}
                      </div>
                      <ArrowRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <select
                        value={accountMappings[account.code] || ''}
                        onChange={e => {
                          setAccountMappings(prev => ({
                            ...prev,
                            [account.code]: e.target.value,
                          }));
                        }}
                        style={{ ...selectStyle, flex: 1, maxWidth: 220 }}
                      >
                        <option value="">(Not mapped)</option>
                        {categoryNames.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      {accountMappings[account.code] && (
                        <CheckCircle2 size={14} style={{ color: '#10b981', flexShrink: 0 }} />
                      )}
                      {!accountMappings[account.code] && (
                        <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  {Object.keys(accountMappings).filter(k => accountMappings[k]).length} of {XERO_ACCOUNT_SUGGESTIONS.length} accounts mapped
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Disconnected state */
        <div style={{
          textAlign: 'center', padding: 'var(--spacing-2xl) var(--spacing-xl)',
          background: 'linear-gradient(180deg, rgba(19,181,234,0.04) 0%, var(--bg-secondary) 100%)',
          borderRadius: 'var(--border-radius-md)',
          border: '1px dashed rgba(19,181,234,0.2)',
        }}>
          <div style={{
            margin: '0 auto var(--spacing-lg)',
            filter: 'drop-shadow(0 4px 12px rgba(19, 181, 234, 0.2))',
          }}>
            <XeroLogo size={56} />
          </div>
          <h3 style={{
            margin: '0 0 var(--spacing-xs)', fontSize: 'var(--font-size-md)',
            fontWeight: 700, color: 'var(--text-primary)',
          }}>
            Connect your Xero account
          </h3>
          <p style={{
            margin: '0 0 var(--spacing-lg)', fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)', maxWidth: 420,
            marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6,
          }}>
            Automatically sync Profit & Loss data from Xero into your project tracking.
            Manual entries will be preserved alongside Xero data.
          </p>
          <button
            className="btn"
            onClick={xero.connect}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #13B5EA 0%, #0078C1 100%)',
              color: '#fff', border: 'none', fontWeight: 600,
              padding: '10px 24px', borderRadius: 8,
              boxShadow: '0 2px 8px rgba(19, 181, 234, 0.3)',
              cursor: 'pointer', transition: 'all 0.2s ease',
            }}
          >
            <ExternalLink size={16} />
            Connect Xero Account
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-lg)',
  padding: 'var(--spacing-xl)',
  paddingTop: 'calc(var(--spacing-xl) + 3px)',
};

const sectionHeaderStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-sm)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--text-primary)',
  display: 'flex',
  alignItems: 'baseline',
};

const mappingRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  padding: '8px 12px',
  background: 'var(--bg-secondary)',
  borderRadius: 'var(--border-radius-sm)',
  border: '1px solid var(--border-color)',
};

const selectStyle: React.CSSProperties = {
  height: 34,
  padding: '0 8px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-sm)',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  fontSize: 'var(--font-size-sm)',
};

const buttonOutlineStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: 'transparent',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-md)',
  cursor: 'pointer',
  color: 'var(--text-primary)',
  fontSize: 'var(--font-size-sm)',
  transition: 'all 0.15s ease',
};
